import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { cursosService, optionsService } from '../services/api';
import CardCurso from '../components/CardCurso';
import HomeFAQ from '../components/HomeFAQ';
import ConfirmPopup from '../components/ConfirmPopup';
import './Home.css';

const FILTRO_TODOS = 'todos';
const FILTRO_DISPONIBLE = 'disponible';

const INFO_CARDS = [
  { id: 1, icon: '👨‍🏫', title: 'Instructores/as', description: 'Destacan por su compromiso y capacidad de transmitir conocimientos de manera clara y efectiva.' },
  { id: 2, icon: '🌐', title: 'Clases Online', description: 'Aprende conocimientos tecnológicos de forma dinámica y flexible desde cualquier lugar.' },
  { id: 3, icon: '✏️', title: 'Proyectos', description: 'Fomentamos la colaboración y el trabajo en equipo a través de proyectos y actividades grupales.' },
  { id: 4, icon: '📖', title: 'Material Online', description: 'Accedé a todas las presentaciones, videos, lecturas y ejercicios en cualquier momento.' },
];

const Home = () => {
  const conoceCursosRef = useRef(null);
  const faqRef = useRef(null);
  const [cursos, setCursos] = useState([]);
  const [loadingCursos, setLoadingCursos] = useState(true);
  const [errorCursos, setErrorCursos] = useState(null);
  const [nivelesCurso, setNivelesCurso] = useState([]);
  const [filtroCursos, setFiltroCursos] = useState(FILTRO_TODOS);

  const scrollToConoceCursos = () => {
    conoceCursosRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToFAQ = () => {
    faqRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    let cancelled = false;
    setErrorCursos(null);
    cursosService
      .getCursosHome()
      .then((data) => {
        if (cancelled) return;
        // Aceptar array directo o objeto con results/data/cursos
        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && typeof data === 'object') {
          list = data.results || data.data || data.cursos || [];
        }
        if (!Array.isArray(list)) list = [];
        setCursos(list);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg =
            err.response?.data?.detail ||
            err.response?.data?.error ||
            (typeof err.response?.data === 'string' ? err.response.data : null) ||
            err.message ||
            'Error al cargar cursos';
          setErrorCursos(msg);
          setCursos([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCursos(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    optionsService.getNivelesCurso()
      .then((data) => setNivelesCurso(Array.isArray(data) ? data : []))
      .catch(() => setNivelesCurso([]));
  }, []);

  const cursosFiltrados = useMemo(() => {
    if (!cursos.length) return [];
    if (filtroCursos === FILTRO_TODOS) return cursos;
    if (filtroCursos === FILTRO_DISPONIBLE) {
      return cursos.filter((c) => {
        const inscripcionAbierta = c.inscripcion_abierta === true;
        const disponiblePorCodigo = c.inscripcion_disponible_por_codigo === true
          || (c.inscripcion_disponible_por_codigo == null && inscripcionAbierta);
        return disponiblePorCodigo;
      });
    }
    const nivelId = Number(filtroCursos);
    if (!Number.isNaN(nivelId)) {
      return cursos.filter((c) => c.nivel != null && Number(c.nivel) === nivelId);
    }
    return cursos;
  }, [cursos, filtroCursos]);

  return (
    <main className="home-raiz">
      {/* Hero */}
      <div
        className="home-hero-raiz"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(32, 32, 29, 0.85) 0%, rgba(45, 45, 45, 0.9) 100%), url(${process.env.PUBLIC_URL || ''}/images/campus.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="home-hero-titulo">
          <h1>CAMPUS</h1>
          <h1>TECNO 3F</h1>
        </div>
        <p>¡Explorá el Futuro Tecnológico en Nuestro Campus Virtual!</p>
        <button type="button" className="home-hero-btn" onClick={scrollToConoceCursos}>
          Ver cursos
        </button>
      </div>

      {/* Sobre el campus */}
      <div className="home-sobre">
        <p className="home-mini">SOBRE EL CAMPUS</p>
        <h2>Les damos la bienvenida al Campus Tecno3F</h2>
        <p className="home-info">
          Te ayudamos a empezar tu proceso de aprendizaje más rápido con itinerarios personalizados que podrás
          recorrer paso a paso, accediendo a los mejores contenidos, recomendados por expertos.
        </p>
      </div>

      {/* Cards de información */}
      <div className="home-infocards">
        <div className="home-card-grid">
          {INFO_CARDS.map((item) => (
            <div key={item.id} className="home-card-info">
              <span className="home-card-info-icon">{item.icon}</span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Sección Conocé los cursos */}
      <div className="home-conoce" ref={conoceCursosRef} id="cursos">
        <p className="home-mini2">CURSOS</p>
        <div className="home-conoce-header">
          <h2>Conocé los cursos</h2>
          <nav className="home-mini-nav">
            <Link to="/dashboard/mis-cursos">Mis Cursos</Link>
          </nav>
        </div>
      </div>

      {/* Filtros: Todos, Disponible, por nivel */}
      {!loadingCursos && cursos.length > 0 && (
        <div className="home-cursos-filtros">
          <button
            type="button"
            className={`home-cursos-filtros-btn ${filtroCursos === FILTRO_TODOS ? 'home-cursos-filtros-btn-active' : ''}`}
            onClick={() => setFiltroCursos(FILTRO_TODOS)}
          >
            Todos
          </button>
          <button
            type="button"
            className={`home-cursos-filtros-btn ${filtroCursos === FILTRO_DISPONIBLE ? 'home-cursos-filtros-btn-active' : ''}`}
            onClick={() => setFiltroCursos(FILTRO_DISPONIBLE)}
          >
            Disponible
          </button>
          {nivelesCurso.map((nivel) => (
            <button
              key={nivel.id}
              type="button"
              className={`home-cursos-filtros-btn ${filtroCursos === Number(nivel.id) ? 'home-cursos-filtros-btn-active' : ''}`}
              onClick={() => setFiltroCursos(Number(nivel.id))}
            >
              {nivel.nombre}
            </button>
          ))}
        </div>
      )}

      {/* Listado de cursos desde el backend */}
      <div className="home-cursos-dispo">
        <ConfirmPopup
          open={Boolean(errorCursos)}
          title="Error"
          message={errorCursos}
          confirmLabel="Aceptar"
          cancelLabel={null}
          onConfirm={() => setErrorCursos(null)}
          onCancel={() => setErrorCursos(null)}
          variant="danger"
        />
        {loadingCursos && (
          <p className="home-cursos-loading">Cargando cursos...</p>
        )}
        {!loadingCursos && !errorCursos && cursos.length === 0 && (
          <p className="home-cursos-empty">
            No se encontraron cursos.
          </p>
        )}
        {!loadingCursos && cursosFiltrados.length > 0 && (
          <div className="home-cursos-grid">
            {cursosFiltrados.map((curso) => (
              <CardCurso key={curso.id} curso={curso} />
            ))}
          </div>
        )}
        {!loadingCursos && !errorCursos && cursos.length > 0 && cursosFiltrados.length === 0 && (
          <p className="home-cursos-empty">No hay cursos que coincidan con el filtro seleccionado.</p>
        )}
      </div>

      {/* FAQ */}
      <div ref={faqRef} id="faq">
        <HomeFAQ />
      </div>
    </main>
  );
};

export default Home;
